import { useEffect, useState } from "react";

import { api, ApiError, type PlayerMergePreview, type PlayerProfile } from "../lib/api";
import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { accountNotOwnedInfo, accountNotOwnedMergeNote, canManagePlayerAccount } from "../lib/playerAccountOwnership";

/** Fusionar un perfil duplicado dentro del perfil correcto (Eduardo 2026-09-19).
 *
 * Se abre SOLO cuando el admin pulsa "Fusionar…" en un jugador (regla de
 * Eduardo: nada aparece sin una acción previa). Flujo: elegir el otro perfil →
 * ver qué se mueve, qué avisos hay y si algo lo impide → confirmar. */

const MOVE_LABELS: Record<string, string> = {
  tournaments: "inscripción(es) a torneos",
  teams: "pareja(s) o equipo(s)",
  orders: "orden(es) de pago",
  ranking_events: "evento(s) de ranking",
  club_memberships: "membresía(s) de club",
  friends: "amistad(es)",
};

const FILL_LABELS: Record<string, string> = {
  last_name: "apellido paterno",
  maternal_last_name: "apellido materno",
  avatar_url: "foto",
  unique_player_id: "ID de jugador",
  phone: "celular",
  email: "correo",
  gender: "género",
  birthdate: "fecha de nacimiento",
  dominant_hand: "mano dominante",
  nationality_code: "nacionalidad",
  national_federation_id: "ID de federación",
  wtn_tennis_id: "Tennis ID (WTN)",
  wtn_rating: "WTN singles",
  wtn_rating_doubles: "WTN dobles",
  backhand_type: "revés",
  club_name: "club",
  state_province: "estado",
  guardian_name: "nombre del tutor",
  guardian_phone: "celular del tutor",
  guardian_relationship: "parentesco del tutor",
  guardian_email: "correo del tutor",
  coach_name: "nombre del coach",
  coach_phone: "celular del coach",
  coach_email: "correo del coach",
  city: "ciudad",
};

interface Props {
  token: string;
  /** El perfil sobre el que se pulsó "Fusionar…". */
  origin: PlayerProfile;
  onClose: () => void;
  onMerged: (message: string) => void;
}

export default function PlayerMergePanel({ token, origin, onClose, onMerged }: Props) {
  const { askConfirm } = useAbortableConfirm([origin.id]);
  const [query, setQuery] = useState(origin.first_name ?? "");
  const [candidates, setCandidates] = useState<PlayerProfile[]>([]);
  const [searching, setSearching] = useState(false);
  const [other, setOther] = useState<PlayerProfile | null>(null);
  // true = se conserva `origin`; false = se conserva el otro.
  const [keepOrigin, setKeepOrigin] = useState(false);
  const [preview, setPreview] = useState<PlayerMergePreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const survivor = other ? (keepOrigin ? origin : other) : null;
  const duplicate = other ? (keepOrigin ? other : origin) : null;

  async function runSearch() {
    const term = query.trim();
    if (term.length < 2) {
      setError("Escribe al menos 2 letras para buscar el otro perfil.");
      return;
    }
    setSearching(true);
    setError(null);
    try {
      const response = await api.players.search(token, { search: term, limit: 12 });
      setCandidates(response.data.players.filter((player) => player.id !== origin.id));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo buscar.");
    } finally {
      setSearching(false);
    }
  }

  useEffect(() => {
    if (!survivor || !duplicate) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setError(null);
    api.players
      .mergePreview(token, { survivor_id: survivor.id, duplicate_id: duplicate.id })
      .then((response) => {
        if (!cancelled) setPreview(response.data);
      })
      .catch((err) => {
        if (cancelled) return;
        setPreview(null);
        setError(err instanceof ApiError ? err.message : "No se pudo calcular la fusión.");
      });
    return () => {
      cancelled = true;
    };
  }, [token, survivor?.id, duplicate?.id]);

  async function handleMerge() {
    if (!survivor || !duplicate || !preview || preview.blockers.length > 0) return;
    const accepted = await askConfirm(
      `¿Fusionar "${duplicate.display_name}" dentro de "${survivor.display_name}"?\n\n` +
        `Todo lo de "${duplicate.display_name}" pasa a "${survivor.display_name}" y ese perfil duplicado queda archivado. No se puede deshacer.`,
      { acceptLabel: "Sí, fusionar perfiles", tone: "danger" },
    );
    if (!accepted) return;
    setBusy(true);
    setError(null);
    try {
      const response = await api.players.merge(token, { survivor_id: survivor.id, duplicate_id: duplicate.id });
      const disabled = response.data.disabled_login ? ` Se desactivó el acceso ${response.data.disabled_login}.` : "";
      onMerged(`Perfiles fusionados: se conserva "${survivor.display_name}".${disabled}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo fusionar.");
    } finally {
      setBusy(false);
    }
  }

  const moves = preview ? Object.entries(preview.moves).filter(([, count]) => count > 0) : [];

  return (
    <div className="inline-confirm" role="dialog" aria-modal="true" aria-label="Fusionar perfiles duplicados">
      <div className="inline-confirm__card player-merge">
        <h3 className="player-merge__title">Fusionar perfiles duplicados</h3>
        <p className="muted">
          Úsalo cuando la MISMA persona quedó con dos perfiles. Todo lo de un perfil pasa al otro y el sobrante se
          archiva; no se borra nada.
        </p>
        <p>
          Perfil seleccionado: <strong>{origin.display_name}</strong>
        </p>

        {!other ? (
          <>
            <label className="player-merge__search">
              <span>Busca el otro perfil de la misma persona</span>
              <input
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    void runSearch();
                  }
                }}
                placeholder="Nombre, correo o celular"
              />
            </label>
            <div className="inline-confirm__actions">
              <button type="button" className="btn btn--ghost" onClick={onClose}>
                No, regresar
              </button>
              <button type="button" className="btn" onClick={() => void runSearch()} disabled={searching}>
                {searching ? "Buscando…" : "Buscar"}
              </button>
            </div>
            {candidates.length > 0 ? (
              <ul className="player-merge__candidates">
                {candidates.map((candidate) => (
                  <li key={candidate.id}>
                    <span>
                      <strong>{candidate.display_name}</strong>
                      <small className="muted">
                        {[candidate.email, candidate.phone, candidate.birthdate].filter(Boolean).join(" · ") || "sin datos de contacto"}
                      </small>
                    </span>
                    {canManagePlayerAccount(candidate) ? (
                      <button type="button" className="btn btn--compact" onClick={() => setOther(candidate)}>
                        Es la misma persona
                      </button>
                    ) : (
                      // Solo se fusionan perfiles que dio de alta tu organización;
                      // entre organizaciones lo hace soporte, y se dice.
                      <small className="muted" data-no-toast>
                        {accountNotOwnedMergeNote(accountNotOwnedInfo(candidate))}
                      </small>
                    )}
                  </li>
                ))}
              </ul>
            ) : null}
          </>
        ) : (
          <>
            <div className="player-merge__roles">
              <p>
                Se <strong>conserva</strong>: {survivor?.display_name}
              </p>
              <p>
                Se <strong>archiva</strong>: {duplicate?.display_name}
              </p>
              <button type="button" className="btn btn--ghost btn--compact" onClick={() => setKeepOrigin((value) => !value)} disabled={busy}>
                Intercambiar cuál se conserva
              </button>
            </div>
            {preview ? (
              <>
                <p className="muted">
                  {moves.length > 0
                    ? `Pasan al perfil conservado: ${moves.map(([key, count]) => `${count} ${MOVE_LABELS[key] ?? key}`).join(", ")}.`
                    : "El perfil que se archiva no tiene inscripciones ni pagos que mover."}
                </p>
                {preview.fills.length > 0 ? (
                  <p className="muted">
                    Datos que se completan (el conservado los tenía vacíos): {preview.fills.map((field) => FILL_LABELS[field] ?? field).join(", ")}.
                  </p>
                ) : null}
                {preview.warnings.map((warning) => (
                  <p key={warning} className="field-warning">⚠️ {warning}</p>
                ))}
                {preview.blockers.map((blocker) => (
                  <p key={blocker} className="player-merge__blocker">⛔ {blocker}</p>
                ))}
              </>
            ) : (
              <p className="muted">Calculando qué se mueve…</p>
            )}
            <div className="inline-confirm__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setOther(null)} disabled={busy}>
                Elegir otro perfil
              </button>
              <button type="button" className="btn btn--ghost" onClick={onClose} disabled={busy}>
                No, regresar
              </button>
              <button
                type="button"
                className="btn btn--danger"
                onClick={() => void handleMerge()}
                disabled={busy || !preview || preview.blockers.length > 0}
              >
                {busy ? "Fusionando…" : "Fusionar"}
              </button>
            </div>
          </>
        )}
        {error ? <p className="player-merge__blocker">{error}</p> : null}
      </div>
    </div>
  );
}
